import { Link } from "wouter";
import { useEnvironments, useProjectGovernancePolicy, useProjects } from "../../api/queries";
import type { Environment, Project } from "../../api/types";
import { EmptyState } from "../../components/AsyncStates";
import { DataTable, type DataTableColumn } from "../../components/DataTable";
import { EnvironmentBadge } from "../../components/EnvironmentBadge";
import { QueryStates } from "../../components/QueryStates";
import { StatePill } from "../../components/StatePill";
import "./govern.css";

/**
 * Govern's "the rules" strand, read-only (docs/product/ui-information-architecture.md §6):
 * an aggregate of what's ALLOWED, sourced from the same two real reads every editing surface
 * itself uses — `useEnvironments` (environment policy: protection/promotion/allowed node
 * types) and `useProjectGovernancePolicy` (a project's declared promotion predicates, ADR-0058
 * introspect). Nothing here is cached or hand-maintained (A-5): every row is a live read, and
 * every row links to its ONE real editing surface — the environment's own detail page in
 * Fleet, or the project's Corpus tab in Develop (where governance is authored in git). This
 * page can never accept an edit itself; there is deliberately no form here.
 *
 * Two facts this aggregate does NOT show because they aren't real fields on the wire contract
 * today (A-10 — no field is fabricated to look more complete than the API actually is):
 * a per-environment "minimum provenance class" (provenance rungs are a project-level fact,
 * `CorpusBinding.expectedRung` — an environment carries no rung of its own), and env-scoped
 * agent/machine identity token SCOPES (ADR-0058 §"the rules" mentions these; no list-scopes-
 * by-environment read exists yet for this client to aggregate). Both are real gaps, not
 * oversights — flagged here rather than silently rendered blank.
 */
export function PolicyTab() {
  return (
    <div className="policy-tab">
      <section className="policy-tab__section" aria-labelledby="policy-environments-heading">
        <h2 id="policy-environments-heading">Environment policy</h2>
        <p className="policy-tab__note">
          Protection, promotion, and node-type admission for every environment — read-only here.
          Edit an environment's policy on its own page in Fleet.
        </p>
        <EnvironmentPolicyTable />
      </section>

      <section className="policy-tab__section" aria-labelledby="policy-projects-heading">
        <h2 id="policy-projects-heading">Declared governance rules</h2>
        <p className="policy-tab__note">
          Each project's own promotion predicates, as declared in its corpus (ADR-0058) —
          read-only here. Edit governance by committing to the project's corpus in Develop.
        </p>
        <ProjectGovernanceList />
      </section>
    </div>
  );
}

const ENV_COLUMNS: DataTableColumn<Environment>[] = [
  {
    key: "name",
    header: "Environment",
    cell: (env) => <EnvironmentBadge name={env.name} envClass={env.class} />,
    className: "data-table__primary",
  },
  {
    key: "protected",
    header: "Protected",
    cell: (env) => (env.protected ? "Yes" : "No"),
  },
  {
    key: "promotion",
    header: "Promotion",
    cell: (env) => (env.requiresPromotion ? "Required (no direct deploys)" : "Direct deploys allowed"),
  },
  {
    key: "nodeTypes",
    header: "Allowed machine types",
    cell: (env) => (env.allowedNodeTypes.length > 0 ? env.allowedNodeTypes.join(", ") : "No restriction"),
    className: "data-table__secondary",
  },
  {
    key: "edit",
    header: <span className="visually-hidden">Edit</span>,
    cell: (env) => (
      <Link to={`/environments/${env.id}`} className="policy-tab__edit-link">
        Edit in Fleet →
      </Link>
    ),
  },
];

function EnvironmentPolicyTable() {
  const { data, isPending, isError, error, refetch } = useEnvironments();
  const items = data?.items ?? [];

  return (
    <QueryStates
      isLoading={isPending}
      loadingLabel="Loading environment policy…"
      isError={isError}
      error={error}
      onRetry={refetch}
      errorDescription={
        error instanceof Error ? error.message : "Couldn't load environment policy. Check your connection and try again."
      }
      isEmpty={Boolean(data && items.length === 0)}
      empty={
        <EmptyState
          title="No environments yet"
          description="Environment policy shows up here once at least one environment exists — create one from Fleet."
        />
      }
    >
      <DataTable columns={ENV_COLUMNS} rows={items} rowKey={(env) => env.id} />
    </QueryStates>
  );
}

function ProjectGovernanceList() {
  const { data, isPending, isError, error, refetch } = useProjects();
  const items = data?.items ?? [];

  return (
    <QueryStates
      isLoading={isPending}
      loadingLabel="Loading declared governance…"
      isError={isError}
      error={error}
      onRetry={refetch}
      errorDescription={
        error instanceof Error ? error.message : "Couldn't load projects. Check your connection and try again."
      }
      isEmpty={Boolean(data && items.length === 0)}
      empty={<EmptyState title="No projects yet" description="A project's declared governance shows up here once it exists." />}
    >
      <ul className="policy-tab__project-list">
        {items.map((project) => (
          <ProjectGovernanceRow key={project.id} project={project} />
        ))}
      </ul>
    </QueryStates>
  );
}

function ProjectGovernanceRow({ project }: { project: Project }) {
  const { data: policy, isPending, isError } = useProjectGovernancePolicy(project.id);

  return (
    <li className="policy-tab__project-row">
      <div className="policy-tab__project-header">
        <Link to={`/projects/${project.id}/corpus`} className="policy-tab__project-name">
          {project.name}
        </Link>
        {!isPending && !isError && policy && (
          <StatePill
            label={policy.governanceConfigured ? "Governance declared" : "No promotion predicates declared"}
            tone={policy.governanceConfigured ? "positive" : "neutral"}
            shape="dot"
          />
        )}
      </div>

      {isPending && <p className="policy-tab__note">Loading…</p>}
      {isError && <p className="policy-tab__note">Couldn't load this project's declared governance.</p>}

      {!isPending && !isError && policy && (
        <>
          {policy.policyError && (
            <p className="policy-tab__policy-error" role="alert">
              Unparseable — every promote is denied fail-closed: {policy.policyError}
            </p>
          )}
          {!policy.policyError && policy.predicates.length > 0 && (
            <ul className="policy-tab__predicates">
              {policy.predicates.map((predicate) => (
                <li key={predicate.id} className="data-table__mono">
                  {predicate.id}: {predicate.values.join(", ")}
                </li>
              ))}
            </ul>
          )}
          {!policy.policyError && policy.predicates.length === 0 && (
            <p className="policy-tab__note">No promotion predicates declared — promotions are ungoverned.</p>
          )}
        </>
      )}

      <Link to={`/projects/${project.id}/corpus`} className="policy-tab__edit-link">
        Edit in Develop → Corpus →
      </Link>
    </li>
  );
}
